import React from 'react';
import { Box, Typography, Button, Stack } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

/**
 * Tarjeta de confirmación de acciones (human-in-the-loop).
 *
 * Cuando el agente quiere ejecutar una acción que MODIFICA datos (guardar un
 * menú, crear una receta, etc.), el backend pausa el turno y emite un evento
 * `action_required`. Esta tarjeta muestra el/los resumen(es) de esas acciones y
 * ofrece al usuario decidir: Aplicar o Cancelar.
 *
 * Props:
 *  - actions: Array<{ summary: string, ... }>  acciones a confirmar.
 *  - onApply: () => void                        confirmar (resume.confirm = true).
 *  - onCancel: () => void                       descartar (resume.confirm = false).
 *  - disabled: boolean                          deshabilita los botones (mientras
 *                                               se reanuda el hilo, p.ej.).
 */
const ActionCard = ({ actions = [], onApply, onCancel, disabled = false }) => {
  if (!actions || actions.length === 0) return null;

  const multiple = actions.length > 1;
  const title = multiple ? '¿Confirmar estas acciones?' : '¿Confirmar esta acción?';

  return (
    <Box
      role="group"
      aria-label={title}
      sx={{
        border: '1px solid #c7d2fe',
        borderRadius: 2.5,
        bgcolor: '#eef2ff',
        p: 1.75,
        mb: 1.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
        <HelpOutlineIcon sx={{ fontSize: 18, color: '#4f46e5' }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#3730a3' }}>
          {title}
        </Typography>
      </Box>

      <Box
        component="ul"
        sx={{ listStyle: 'none', p: 0, m: 0, mb: 1.5 }}
      >
        {actions.map((action, idx) => (
          <Box
            component="li"
            key={action?.actionId ?? idx}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 0.75,
              mb: idx < actions.length - 1 ? 0.5 : 0,
            }}
          >
            <CheckCircleOutlineIcon sx={{ fontSize: 16, color: '#6366f1', mt: '2px' }} />
            <Typography variant="body2" sx={{ color: '#1e293b', lineHeight: 1.45 }}>
              {action?.summary || 'Acción sin descripción.'}
            </Typography>
          </Box>
        ))}
      </Box>

      <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
        <Button
          variant="text"
          size="small"
          onClick={onCancel}
          disabled={disabled}
          sx={{
            color: '#64748b',
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': { bgcolor: '#e2e8f0' },
          }}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={onApply}
          disabled={disabled}
          disableElevation
          sx={{
            bgcolor: '#4f46e5',
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': { bgcolor: '#4338ca' },
            '&.Mui-disabled': { bgcolor: '#c7d2fe', color: '#fff' },
          }}
        >
          Aplicar
        </Button>
      </Stack>
    </Box>
  );
};

export default ActionCard;
